import { useEffect, useRef } from "react"
import ReactMarkdown from "react-markdown"

import { CoviVizChartBody, parseChartSpec } from "@/app-covi"
import {
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Input,
  SofiaMark,
  Textarea,
} from "@coval/ui"
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  Database,
  ExternalLink,
  Plus,
  Trash2,
} from "lucide-react"
import remarkGfm from "remark-gfm"

import { formatDate } from "../../utils/reportClient"
import {
  OVERVIEW_TARGET,
  type ReportSectionProgress,
} from "../../utils/reportRevisionTypes"
import type { Notebook, NotebookSection } from "../../utils/reportTypes"
import { CoverageNotice } from "./CoverageNotice"
import { ReportChartEditor } from "./ReportChartEditor"
import { ReportSourceData } from "./ReportSourceData"
import { reportClass } from "./ReportStyles"
import { SectionRevision } from "./SectionRevision"

function safeUrl(value: string) {
  try {
    return ["https:", "http:"].includes(new URL(value).protocol)
      ? value
      : undefined
  } catch {
    return undefined
  }
}

function Coverage({ coverage }: { coverage: Record<string, unknown> }) {
  const display = [
    ["Records in scope", coverage.scope_records],
    ["Records this week", coverage.current_week_records],
    ["Selected agents", coverage.selected_agent_count],
    ["Observed numeric metrics", coverage.metric_count_available],
    [
      "Requested history starts",
      typeof coverage.history_start === "string"
        ? formatDate(coverage.history_start)
        : undefined,
    ],
    [
      "Data captured",
      typeof coverage.extracted_at === "string"
        ? `${formatDate(coverage.extracted_at, true)} PT`
        : undefined,
    ],
  ].filter((entry): entry is [string, string | number | boolean] =>
    ["string", "number", "boolean"].includes(typeof entry[1]),
  )
  return (
    <dl className={reportClass("coverage-grid")}>
      {display.slice(0, 12).map(([key, value]) => (
        <div key={key}>
          <dt>{key.replaceAll("_", " ")}</dt>
          <dd>
            {typeof value === "number" ? value.toLocaleString() : String(value)}
          </dd>
        </div>
      ))}
    </dl>
  )
}

export function NotebookDocument({
  notebook,
  editing,
  onChange,
  disabled,
  revisionProgress = {},
  onReviseSection,
  highlight,
}: {
  notebook: Notebook
  editing: boolean
  onChange: (notebook: Notebook) => void
  disabled: boolean
  revisionProgress?: Record<string, ReportSectionProgress>
  onReviseSection?: (id: string) => void
  highlight?: { targets: string[]; sequence: number }
}) {
  const articleRef = useRef<HTMLElement>(null)
  useEffect(() => {
    if (highlight?.targets.length) {
      const changed = articleRef.current?.querySelector<HTMLElement>(
        '[data-changed="true"]',
      )
      const heading = changed?.querySelector<HTMLElement>("h2, h3")
      ;(heading ?? changed)?.scrollIntoView({
        block: "center",
        behavior: "instant",
      })
    }
  }, [highlight])
  const overviewProgress = revisionProgress[OVERVIEW_TARGET]
  const overviewUpdating = ["queued", "running"].includes(
    overviewProgress?.status ?? "",
  )
  const overviewDraft =
    overviewProgress?.status === "running" ? overviewProgress.body : undefined
  const updateSection = (index: number, section: NotebookSection) =>
    onChange({
      ...notebook,
      sections: notebook.sections.map((current, position) =>
        position === index ? section : current,
      ),
    })
  const move = (index: number, direction: number) => {
    const sections = [...notebook.sections]
    const target = index + direction
    if (target < 0 || target >= sections.length) {
      return
    }
    ;[sections[index], sections[target]] = [sections[target], sections[index]]
    onChange({ ...notebook, sections })
  }
  return (
    <article
      ref={articleRef}
      className={reportClass("notebook-document")}
      data-font={notebook.presentation?.font}
      data-accent={notebook.presentation?.accent}
      data-density={notebook.presentation?.density}
    >
      <header
        key={`overview-${highlight?.sequence ?? 0}`}
        className={reportClass("document-header")}
        data-changed={highlight?.targets.includes(OVERVIEW_TARGET) || undefined}
      >
        <div className={reportClass("eyebrow")}>
          <SofiaMark size={20} />
          Sofia notebook
        </div>
        <div className={reportClass("period-label")}>
          {formatDate(notebook.period.start)} –{" "}
          {formatDate(
            new Date(
              new Date(notebook.period.end).getTime() - 1000,
            ).toISOString(),
          )}{" "}
          <span>·</span> Pacific time
        </div>
        {editing ? (
          <Input
            className={reportClass("title-input")}
            aria-label="Notebook title"
            value={notebook.title}
            disabled={disabled || overviewUpdating}
            onChange={(event) =>
              onChange({ ...notebook, title: event.target.value })
            }
          />
        ) : (
          <h2>{notebook.title}</h2>
        )}
        {editing ? (
          <Textarea
            aria-label="Notebook summary"
            className={reportClass("summary-input")}
            rows={4}
            disabled={disabled || overviewUpdating}
            value={notebook.summary}
            onChange={(event) =>
              onChange({ ...notebook, summary: event.target.value })
            }
          />
        ) : (
          <div
            className={reportClass(
              `document-summary markdown ${overviewDraft ? "revision-draft" : ""}`,
            )}
          >
            {overviewDraft && (
              <p className={reportClass("revision-draft-label")}>
                Draft · waiting for the saved revision
              </p>
            )}
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {overviewDraft || notebook.summary}
            </ReactMarkdown>
          </div>
        )}
        <SectionRevision
          targetId={OVERVIEW_TARGET}
          title="overview"
          progress={overviewProgress}
          onRevise={onReviseSection}
          disabled={disabled}
        />
        <div className={reportClass("document-meta")}>
          {notebook.coverage.comparison_available !== false && (
            <span>
              Compared with {formatDate(notebook.period.previous_start)} –{" "}
              {formatDate(
                new Date(
                  new Date(notebook.period.start).getTime() - 1000,
                ).toISOString(),
              )}
            </span>
          )}
          <span>Revision {notebook.revision}</span>
        </div>
        <CoverageNotice coverage={notebook.coverage} />
      </header>
      <div className={reportClass("document-sections")}>
        {notebook.sections.map((section, index) => {
          const progress = revisionProgress[section.id]
          const updating = ["queued", "running"].includes(
            progress?.status ?? "",
          )
          const provisionalBody =
            progress?.status === "running" ? progress.body : undefined
          const spec = section.chart
            ? parseChartSpec(section.chart, { allowReportColors: true })
            : null
          const evidence = notebook.source_refs
            .filter((source) => section.evidence_ids?.includes(source.id))
            .filter(
              (source, index, sources) =>
                sources.findIndex((item) => item.url === source.url) === index,
            )
          return (
            <section
              key={`${section.id}-${highlight?.sequence ?? 0}`}
              className={reportClass("document-section")}
              data-changed={
                highlight?.targets.includes(section.id) || undefined
              }
            >
              <div className={reportClass("section-heading")}>
                <span className={reportClass("section-number")}>
                  {String(index + 1).padStart(2, "0")}
                </span>
                {editing ? (
                  <Input
                    aria-label={`Section ${index + 1} title`}
                    value={section.title}
                    disabled={disabled || updating}
                    onChange={(event) =>
                      updateSection(index, {
                        ...section,
                        title: event.target.value,
                      })
                    }
                  />
                ) : (
                  <h3>{section.title}</h3>
                )}
                {editing && (
                  <div className={reportClass("section-actions")}>
                    <Button
                      variant="ghost"
                      icon
                      tooltip={`Move section ${index + 1} up`}
                      disabled={disabled || updating || index === 0}
                      onClick={() => move(index, -1)}
                    >
                      <ArrowUp />
                    </Button>
                    <Button
                      variant="ghost"
                      icon
                      tooltip={`Move section ${index + 1} down`}
                      disabled={
                        disabled ||
                        updating ||
                        index === notebook.sections.length - 1
                      }
                      onClick={() => move(index, 1)}
                    >
                      <ArrowDown />
                    </Button>
                    <Button
                      variant="ghost"
                      icon
                      tooltip={`Remove section ${index + 1}`}
                      disabled={disabled || updating}
                      onClick={() =>
                        onChange({
                          ...notebook,
                          sections: notebook.sections.filter(
                            (_, position) => position !== index,
                          ),
                        })
                      }
                    >
                      <Trash2 />
                    </Button>
                  </div>
                )}
              </div>
              <SectionRevision
                targetId={section.id}
                title={section.title}
                progress={progress}
                onRevise={onReviseSection}
                disabled={disabled}
              />
              {editing ? (
                <Textarea
                  aria-label={`Section ${index + 1} text`}
                  rows={5}
                  disabled={disabled || updating}
                  value={section.body}
                  onChange={(event) =>
                    updateSection(index, {
                      ...section,
                      body: event.target.value,
                    })
                  }
                />
              ) : (
                <div
                  className={reportClass(
                    `markdown section-prose ${provisionalBody ? "revision-draft" : ""}`,
                  )}
                >
                  {provisionalBody && (
                    <p className={reportClass("revision-draft-label")}>
                      {spec
                        ? "Draft · saved chart stays visible until validation completes"
                        : "Draft · waiting for the saved revision"}
                    </p>
                  )}
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {provisionalBody || section.body}
                  </ReactMarkdown>
                </div>
              )}
              {spec ? (
                <Card className={reportClass("chart-card")}>
                  <CardHeader>
                    <CardTitle>{spec.title}</CardTitle>
                    {spec.subtitle && (
                      <CardDescription>{spec.subtitle}</CardDescription>
                    )}
                  </CardHeader>
                  <CoviVizChartBody
                    reportColors
                    spec={spec}
                    height={Math.max(
                      260,
                      spec.opts?.horizontal ? spec.data.length * 38 + 80 : 280,
                    )}
                  />
                  <ReportSourceData spec={spec} />
                </Card>
              ) : (
                section.chart && (
                  <p className={reportClass("inline-error")}>
                    This chart did not pass validation. Its text and source
                    references are still available.
                  </p>
                )
              )}
              {editing && section.chart && (
                <ReportChartEditor
                  disabled={disabled || updating}
                  section={section}
                  datasets={notebook.datasets ?? []}
                  onChange={(updated) => updateSection(index, updated)}
                />
              )}
              {evidence.length > 0 && (
                <div className={reportClass("evidence-links")}>
                  {evidence.map((source) => (
                    <Button
                      key={source.id}
                      variant="secondary"
                      asChild
                      className="h-auto max-w-full whitespace-normal px-2 py-1 text-xs"
                    >
                      <a
                        href={safeUrl(source.url)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <ExternalLink size={11} />
                        {source.label}
                      </a>
                    </Button>
                  ))}
                </div>
              )}
            </section>
          )
        })}
      </div>
      {editing && (
        <Button
          variant="secondary"
          disabled={disabled}
          onClick={() =>
            onChange({
              ...notebook,
              sections: [
                ...notebook.sections,
                {
                  id: crypto.randomUUID(),
                  title: "New section",
                  body: "",
                  evidence_ids: [],
                },
              ],
            })
          }
        >
          <Plus />
          Add text section
        </Button>
      )}
      {Boolean(notebook.caveats?.length) && (
        <section
          key={`caveats-${highlight?.sequence ?? 0}`}
          className={reportClass("caveats")}
          data-changed={highlight?.targets.includes("__caveats__") || undefined}
        >
          <h3>Keep in mind</h3>
          <ul>
            {notebook.caveats?.map((caveat) => (
              <li key={caveat}>{caveat}</li>
            ))}
          </ul>
        </section>
      )}
      <footer className={reportClass("provenance")}>
        <div className={reportClass("panel-heading")}>
          <Database size={14} />
          <h3>Sources & methodology</h3>
        </div>
        <p>
          Generated {formatDate(notebook.created_at, true)} Pacific time ·{" "}
          {notebook.model || "Model not recorded"} ·{" "}
          {notebook.generation_status?.replaceAll("_", " ")}
        </p>
        <p>
          AI narrative: review against evidence. Chart values are validated
          against the saved datasets; prose remains reviewable.
        </p>
        <Coverage coverage={notebook.coverage ?? {}} />
        {notebook.source_refs.length > 0 && (
          <Collapsible className="my-4">
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                className="max-w-full whitespace-normal text-left [&[data-state=open]>svg]:rotate-180"
              >
                <ChevronDown />
                All {notebook.source_refs.length} evidence references
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <ul>
                {notebook.source_refs.map((source) => (
                  <li key={source.id}>
                    <Button
                      variant="link"
                      asChild
                      className="max-w-full whitespace-normal text-left"
                    >
                      <a
                        href={safeUrl(source.url)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {source.label} <ExternalLink size={11} />
                      </a>
                    </Button>
                  </li>
                ))}
              </ul>
            </CollapsibleContent>
          </Collapsible>
        )}
        <p className={reportClass("field-hint")}>
          This artifact preserves its data window and revision. A later issue is
          generated separately; it does not rewrite this one.
        </p>
      </footer>
    </article>
  )
}
